import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Users, Search, ChevronUp, ChevronDown, Upload, Download } from 'lucide-react';
import { useContacts, useDeleteContact } from '../hooks/useContacts.js';
import { useCompanies } from '../hooks/useCompanies.js';
import { toast } from '../lib/toast.js';
import type { Contact } from '@mayksel/types';
import ContactModal from '../components/contacts/ContactModal.js';
import CsvImportModal from '../components/dataManagement/CsvImportModal.js';
import ViewBar from '../components/views/ViewBar.js';
import type { ColumnDef } from '../components/views/ColumnPicker.js';
import type { FieldDef } from '../components/views/FilterPanel.js';
import { downloadCsv } from '../lib/csvExport.js';
import { useFeatureFlag } from '../lib/posthog.js';

type SortDir = 'asc' | 'desc';

const CONTACT_COLUMNS: ColumnDef[] = [
  { key: 'name', label: 'Name', required: true },
  { key: 'company', label: 'Company' },
  { key: 'title', label: 'Title' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
];

const CONTACT_FIELD_DEFS: FieldDef[] = [
  { key: 'name', label: 'Name', type: 'text' },
  { key: 'title', label: 'Title', type: 'text' },
  { key: 'email', label: 'Email', type: 'text' },
];

export default function Contacts() {
  const { t } = useTranslation('contacts');
  const { t: tdm } = useTranslation('dataManagement');
  const navigate = useNavigate();
  const dataManagementEnabled = useFeatureFlag('data_management');

  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [sortBy, setSortBy] = useState('last_name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const { data, isLoading, error } = useContacts({
    ...(search ? { search } : {}),
    ...(companyFilter ? { companyId: companyFilter } : {}),
    sortBy,
    sortDir,
  });

  const { data: companiesData } = useCompanies({ limit: 200 });
  const deleteContact = useDeleteContact();

  const handleSort = useCallback((col: string) => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
  }, [sortBy]);

  function handleExport() {
    if (!data?.data.length) return;
    const rows = data.data.map((c) => ({
      firstName: c.firstName,
      lastName: c.lastName,
      email: c.email ?? '',
      phone: c.phone ?? '',
      title: c.title ?? '',
      company: c.companies?.find((co) => co.isPrimary)?.companyName ?? c.companies?.[0]?.companyName ?? '',
    }));
    downloadCsv(rows, 'contacts');
    toast.success(tdm('export.button'));
  }

  async function handleDelete(contact: Contact) {
    const name = `${contact.firstName} ${contact.lastName}`;
    if (!confirm(t('detail.delete_confirm', { name }))) return;
    try {
      await deleteContact.mutateAsync(contact.id);
      toast.success(t('toast.deleted'));
    } catch (_err) {
      toast.error(t('toast.delete_error'));
    }
  }

  function handleEdit(contact: Contact) {
    setEditingContact(contact);
    setModalOpen(true);
  }

  function handleModalClose() {
    setModalOpen(false);
    setEditingContact(null);
  }

  const SortIcon = ({ col }: { col: string }) => {
    if (sortBy !== col) return null;
    return sortDir === 'asc'
      ? <ChevronUp size={14} style={{ marginLeft: 4 }} />
      : <ChevronDown size={14} style={{ marginLeft: 4 }} />;
  };

  return (
    <div className="page-enter">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-6)' }}>
        <h1 className="page-title">{t('list.title')}</h1>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          {dataManagementEnabled && (
            <>
              <button onClick={() => setImportOpen(true)} style={secondaryBtnStyle}>
                <Upload size={14} />
                {tdm('import.button')}
              </button>
              <button
                onClick={handleExport}
                disabled={!data?.data.length}
                style={{ ...secondaryBtnStyle, opacity: data?.data.length ? 1 : 0.5 }}
              >
                <Download size={14} />
                {tdm('export.button')}
              </button>
            </>
          )}
          <button
            onClick={() => setModalOpen(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
              padding: 'var(--space-2) var(--space-4)', background: 'var(--color-brand)',
              color: 'var(--color-text-inverse)', border: 'none',
              borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-medium)', cursor: 'pointer',
            }}
          >
            + {t('list.add_button')}
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 'var(--space-3)', marginBottom: 'var(--space-4)', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Search size={16} style={{
            position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
            color: 'var(--color-text-muted)', pointerEvents: 'none',
          }} />
          <input
            type="text" value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('list.search_placeholder')}
            style={{
              width: '100%', paddingLeft: 36, paddingRight: 'var(--space-3)',
              paddingTop: 'var(--space-2)', paddingBottom: 'var(--space-2)',
              border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
              fontSize: 'var(--text-sm)', color: 'var(--color-text)',
              background: 'var(--color-bg)', boxSizing: 'border-box',
            }}
          />
        </div>

        <select
          value={companyFilter}
          onChange={(e) => setCompanyFilter(e.target.value)}
          aria-label={t('list.filter_company')}
          style={{
            padding: 'var(--space-2) var(--space-3)', border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
            color: 'var(--color-text)', background: 'var(--color-bg)',
          }}
        >
          <option value="">{t('list.all_companies')}</option>
          {companiesData?.data.map((co) => (
            <option key={co.id} value={co.id}>{co.name}</option>
          ))}
        </select>
      </div>

      {/* View bar */}
      <div style={{ marginBottom: 'var(--space-4)' }}>
        <ViewBar
          entityType="contact"
          columnDefs={CONTACT_COLUMNS}
          fieldDefs={CONTACT_FIELD_DEFS}
          onViewChange={() => undefined}
        />
      </div>

      {/* Loading */}
      {isLoading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="skeleton-line" style={{ height: 52, borderRadius: 'var(--radius-md)' }} />
          ))}
        </div>
      )}

      {/* Error */}
      {error && !isLoading && (
        <div style={{
          padding: 'var(--space-6)', background: 'var(--color-danger-bg)',
          borderRadius: 'var(--radius-lg)', color: 'var(--color-danger-text)',
          fontSize: 'var(--text-sm)', textAlign: 'center',
        }}>
          {t('errors.generic', { ns: 'common' })}
        </div>
      )}

      {/* Empty */}
      {!isLoading && !error && data?.data.length === 0 && (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', padding: 'var(--space-16) var(--space-8)',
          textAlign: 'center', border: '1px dashed var(--color-border)',
          borderRadius: 'var(--radius-xl)',
        }}>
          <Users size={48} style={{ marginBottom: 'var(--space-4)', color: 'var(--color-gray-300)' }} />
          <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)', marginBottom: 'var(--space-2)' }}>
            {search || companyFilter ? t('empty.no_results', { ns: 'common' }) : t('list.empty_title')}
          </h2>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', maxWidth: 320, marginBottom: 'var(--space-5)' }}>
            {search || companyFilter ? t('empty.try_adjusting', { ns: 'common' }) : t('list.empty_body')}
          </p>
          {!search && !companyFilter && (
            <button
              onClick={() => setModalOpen(true)}
              style={{
                padding: 'var(--space-2) var(--space-5)', background: 'var(--color-brand)',
                color: 'var(--color-text-inverse)', border: 'none',
                borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)', cursor: 'pointer',
              }}
            >
              + {t('list.empty_cta')}
            </button>
          )}
        </div>
      )}

      {/* Table */}
      {!isLoading && !error && data && data.data.length > 0 && (
        <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', background: 'var(--color-bg)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--color-bg-subtle)', borderBottom: '1px solid var(--color-border)' }}>
                {[
                  { key: 'last_name', label: t('columns.name') },
                  { key: 'company', label: t('columns.company'), noSort: true },
                  { key: 'title', label: t('columns.title'), noSort: true },
                  { key: 'email', label: t('columns.email') },
                  { key: 'phone', label: t('columns.phone'), noSort: true },
                ].map(({ key, label, noSort }) => (
                  <th
                    key={key}
                    onClick={() => !noSort && handleSort(key)}
                    style={{
                      padding: 'var(--space-3) var(--space-4)', textAlign: 'left',
                      fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)',
                      color: 'var(--color-text-secondary)', textTransform: 'uppercase',
                      letterSpacing: 'var(--tracking-wide)', cursor: noSort ? 'default' : 'pointer',
                      userSelect: 'none', whiteSpace: 'nowrap',
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      {label}
                      <SortIcon col={key} />
                    </span>
                  </th>
                ))}
                <th style={{ width: 80 }} />
              </tr>
            </thead>
            <tbody>
              {data.data.map((contact) => {
                const primaryLink = contact.companies?.find((c) => c.isPrimary) ?? contact.companies?.[0];
                return (
                  <tr
                    key={contact.id}
                    onClick={() => navigate(`/contacts/${contact.id}`)}
                    style={{ borderBottom: '1px solid var(--color-gray-100)', cursor: 'pointer', transition: 'background var(--duration-snap)' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-bg-subtle)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = '')}
                  >
                    <td style={{ padding: 'var(--space-3) var(--space-4)' }}>
                      <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)' }}>
                        {contact.firstName} {contact.lastName}
                      </p>
                      {contact.title && (
                        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{contact.title}</p>
                      )}
                    </td>
                    <td style={{ padding: 'var(--space-3) var(--space-4)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                      {primaryLink ? (
                        <span>
                          {primaryLink.companyName}
                          {(contact.companies?.length ?? 0) > 1 && (
                            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginLeft: 'var(--space-1)' }}>
                              +{(contact.companies?.length ?? 0) - 1}
                            </span>
                          )}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>{t('list.no_company')}</span>
                      )}
                    </td>
                    <td style={{ padding: 'var(--space-3) var(--space-4)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                      {contact.title ?? '—'}
                    </td>
                    <td style={{ padding: 'var(--space-3) var(--space-4)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                      {contact.email ? (
                        <a href={`mailto:${contact.email}`} onClick={(e) => e.stopPropagation()}
                          style={{ color: 'var(--color-brand)', textDecoration: 'none' }}>
                          {contact.email}
                        </a>
                      ) : '—'}
                    </td>
                    <td style={{ padding: 'var(--space-3) var(--space-4)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                      {contact.phone ?? '—'}
                    </td>
                    <td style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
                        <button onClick={() => handleEdit(contact)} style={actionBtnStyle} aria-label={`Edit ${contact.firstName}`}>
                          {t('detail.edit_button')}
                        </button>
                        <button onClick={() => handleDelete(contact)}
                          style={{ ...actionBtnStyle, color: 'var(--color-danger)' }}
                          aria-label={`Delete ${contact.firstName}`}>
                          {t('actions.delete', { ns: 'common' })}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {data.total > 50 && (
            <div style={{
              padding: 'var(--space-3) var(--space-4)', borderTop: '1px solid var(--color-border)',
              fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)',
            }}>
              {t('pagination.showing', { ns: 'common', from: 1, to: data.data.length, total: data.total })}
            </div>
          )}
        </div>
      )}

      {modalOpen && (
        <ContactModal contact={editingContact} onClose={handleModalClose} />
      )}

      {importOpen && (
        <CsvImportModal entityType="contact" onClose={() => setImportOpen(false)} />
      )}
    </div>
  );
}

const actionBtnStyle: React.CSSProperties = {
  padding: '4px 8px', background: 'none', border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-xs)',
  color: 'var(--color-text-secondary)', cursor: 'pointer',
};

const secondaryBtnStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 'var(--space-1)',
  padding: 'var(--space-2) var(--space-3)',
  background: 'none', border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
  color: 'var(--color-text-secondary)', cursor: 'pointer',
};
